/**
 * Reads off the body registry: the focused row, the ladder body, datum and
 * relief radii, the imagery and corridor floors a body's own data stops the
 * descent at, its season, and the osculating element sets a view's bodies
 * still need.
 */
import * as THREE from 'three';

import { tierAt } from './camera.ts';
import { corridorBandIndexAt, corridorGeometry, type CorridorGeometry } from './corridor.ts';
import { softClimate, type PlotClimate } from './plotField.ts';
import { albedoGsd, imageryFloorAltitudeM } from './tileTree.ts';
import { ensureOsculating } from '../world/bodies.ts';
import type { BakeGlobalProduct, BodyRow, Quadrangle } from '../world/types';
import type { AtlasLive, AtlasState, ImageryFloor } from '../atlas';
import type { BodySceneHandle } from './bodyScene.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { SurfaceRadiusHandle } from './surfaceRadius.ts';

export interface BodyReadersDeps {
  readonly state: AtlasState;
  readonly urlBodyId: { id: string; explicit: boolean };
  readonly bodyScene: BodySceneHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly live: AtlasLive;
  /** Built after this module, so atlas.ts hands it over through a getter. */
  readonly focusRig: FocusRigHandle;
}

export interface BodyReadersHandle {
  bodyMesh(): THREE.Mesh | null;
  rowById(id: string | null): BodyRow | undefined;
  requestSets(id: string | null): void;
  ladderBody(): string;
  registryDatumM(id: string | null): number;
  reliefAboveDatumM(id: string | null, datumM: number): number;
  bodyQuads(): readonly Quadrangle[];
  quadFor(lat: number, lon: number): Quadrangle | undefined;
  descendable(): boolean;
  readonly imageryFloorDebug: { heightRoleFrom: string | null; ignoreSmoothGround: boolean };
  smoothGround(id: string | null): boolean;
  readonly artificialDebug: {
    dropTileRole: boolean; legacyTileFill: boolean; reliefGlobeOnly: boolean;
  };
  imageryFloor(id: string | null): ImageryFloor | null;
  shapeModelProduct(id: string | null): BakeGlobalProduct | null;
  readonly corridorDebug: { liftLowestBottom: boolean };
  corridorCache: { id: string; lifted: boolean; geo: CorridorGeometry } | null;
  readonly corridorBuilds: number;
  corridorFor(id: string | null): CorridorGeometry | null;
  corridorBandNow(): number | null;
  descentFloorM(id: string | null): number | null;
  floorTier(): number;
  bodyClimate(): PlotClimate | null;
}

export function createBodyReaders(deps: BodyReadersDeps): BodyReadersHandle {
  const { state, urlBodyId } = deps;

  const bodyMesh = (): THREE.Mesh | null => deps.bodyScene.state.bodyNode?.mesh ?? null;
  const rowById = (id: string | null): BodyRow | undefined =>
    id === null ? undefined : deps.bodyScene.state.bodyRows.find((r) => r.id === id);

  /** Bodies whose own system has already been named, so already asked for. */
  const setsAsked = new Set<string>();

  /**
   * The osculating element sets a view of this body's system needs.
   *
   * The sidecar is one file per sampled row and only the heliocentric ones
   * come down with the registry - phobos and deimos are 93 percent of its
   * bytes and nothing outside the Mars system can draw the difference. A view
   * draws a whole system, so naming one body asks for its satellites and its
   * siblings too, and the sets land in the rows every view is already holding.
   */
  function requestSets(id: string | null): void {
    if (id === null || deps.bodyScene.state.bodyRows.length === 0 || setsAsked.has(id)) return;
    setsAsked.add(id);
    const parent = rowById(id)?.parent ?? null;
    const ids = new Set<string>([id]);
    for (const r of deps.bodyScene.state.bodyRows) {
      if (r.parent === id || (parent !== null && r.parent === parent)) ids.add(r.id);
    }
    void ensureOsculating(deps.bodyScene.state.bodyRows, [...ids]).then(() => {
      // A body is placed from the live elements every frame but its ring is
      // sampled once and tracked; a ring still on the secular fit stands
      // deimos 7.8 px off it, which `orbit-rings --rings-epoch` reads.
      //
      // Refreshed over the whole batch rather than only the rows that gained
      // sets: a target with nothing to fetch (a heliocentric id, or a
      // parent-relative one already attached) still forces every sibling ring
      // stale. Left unnarrowed on purpose - `setsAsked` already bounds it to
      // once per id, and `--rings-epoch` reads the cost inside its own noise
      // (well under a tenth of a millisecond a frame) - rather than tracking
      // which of `ids` actually changed for a saving that small.
      for (const rowId of ids) deps.bodyScene.state.orrery?.refreshElements(rowId);
      deps.live.needsRender = true;
      // A dropped fetch resolves rather than rejects (bodies.ts forgets the id
      // so it stays fetchable there), so a row still missing its sets after
      // this settles must clear every id THIS call marked asked - not just the
      // one named - or a repeat naming that same id never retries the row.
      const stillMissing = [...ids].some((rowId) => {
        const row = rowById(rowId);
        return row?.orbit?.osculating !== undefined && row.orbit.sets === undefined;
      });
      if (stillMissing) for (const askedId of ids) setsAsked.delete(askedId);
    }).catch((err: unknown) => {
      for (const askedId of ids) setsAsked.delete(askedId);
      console.warn(`[atlas] the osculating sets for "${id}" did not attach`, err);
    });
  }

  /**
   * The body the ladder below the orrery is on.
   *
   * `state.bodyId` once one is picked, and the URL's own answer before that:
   * the heliocentric view focuses nothing, and the globe, the pyramid, the sky
   * and the sites all still have to know which body they are about.
   */
  const ladderBody = (): string => state.bodyId ?? urlBodyId.id;

  /**
   * A body's datum radius from the registry, metres.
   *
   * The equatorial radius, which is the figure its map products are gridded on:
   * Mars's areoid at 3,396,190 m, 608 m over the mean radius in `radius_m`. It
   * is what the overlay geometry is built against and what a band edge is
   * derived from before the pyramid's own manifest lands. Zero until the
   * registry does, which is a frame with none of that drawn on it.
   */
  const registryDatumM = (id: string | null): number => rowById(id)?.ellipsoid.a_m ?? 0;

  /**
   * How far a body's ground stands over its pyramid's datum, metres.
   *
   * The tile tree's horizon over-includes by this much, and its own default is
   * one percent of the radius - Mars's 34 km against Olympus Mons's 21, which
   * is the right order for a body that is round. Phobos is not: its pyramid is
   * relief about an 11,100 m sphere and its long axis stands 1,900 m over that,
   * so one percent is 111 m and the horizon stops asking for tiles a third of
   * the way in from the limb. The registry's triaxial ellipsoid is the body's
   * own statement of that departure, and the percentage stays as the floor for
   * a round body, whose peaks the ellipsoid says nothing about.
   */
  function reliefAboveDatumM(id: string | null, datumM: number): number {
    const e = rowById(id)?.ellipsoid;
    const bulge = e === undefined ? 0 : Math.max(e.a_m, e.b_m, e.c_m) - datumM;
    return Math.max(datumM * 0.01, bulge);
  }

  /** The quadrangles the ladder's body publishes; empty for a body with none. */
  const bodyQuads = (): readonly Quadrangle[] => rowById(ladderBody())?.quadrangles ?? [];

  /**
   * The quadrangle a point actually falls in.
   *
   * The breadcrumb used to carry whichever quad the player clicked three tiers
   * ago on a SYNTHETIC hex ring, which is how it came to read "MC-09 Tharsis"
   * over ground in Jezero. A quadrangle is a fact about coordinates.
   */
  function quadFor(lat: number, lon: number): Quadrangle | undefined {
    const lonN = ((lon % 360) + 360) % 360;
    return bodyQuads().find(
      (q) => lat >= q.latMin && (lat < q.latMax || q.latMax >= 90)
        && lonN >= q.lonMin && lonN < q.lonMax,
    );
  }

  /** Whether the open body has ground to descend to, per the registry. */
  const descendable = (): boolean => rowById(state.bodyId)?.availability.descend ?? false;

  /**
   * `descent-dojo --inject height-role`: the imagery floor's own control.
   *
   * One hook for both halves because they are one statement - the row says the
   * body has elevation, so the manifest has to be able to serve it. The height
   * role is spliced in from the named body's pyramid and its tiles are read
   * from there, which lifts the stop and puts height on the wire.
   *
   * `ignoreSmoothGround` is `descent-dojo --stand --inject no-smooth-ground`:
   * the row's `availability.ground` read as absent, which is the build before
   * it existed - the descent stops at the imagery floor again.
   */
  const imageryFloorDebug: { heightRoleFrom: string | null; ignoreSmoothGround: boolean } = {
    heightRoleFrom: null, ignoreSmoothGround: false,
  };

  /**
   * Whether this body's descent goes on past its imagery floor to standing
   * height on the datum sphere, with no elevation under it.
   */
  function smoothGround(id: string | null): boolean {
    if (imageryFloorDebug.ignoreSmoothGround) return false;
    return rowById(id)?.availability.ground === 'smooth';
  }

  /**
   * `descent-dojo --artificial`'s three tile-band controls.
   *
   * `dropTileRole` reopens the pyramid with the artificial block gone, the
   * way a deployed bucket that predates `sync_tiles.py --role artificial`
   * serves it - reproducing the south-crossing blanking from a tree that
   * otherwise holds every tile locally. `legacyTileFill` leaves the streamed
   * role alone but skips the globe-map fallback a tile with no streamed fill
   * would otherwise composite, which is the pre-fix behaviour: nothing drawn
   * at all over such a tile's invented ground. `reliefGlobeOnly` builds the
   * tile materials without the relief chunk, which leaves the shading where it
   * stopped before: on the globe, and gone the moment the pyramid opens.
   */
  const artificialDebug: {
    dropTileRole: boolean; legacyTileFill: boolean; reliefGlobeOnly: boolean;
  } = { dropTileRole: false, legacyTileFill: false, reliefGlobeOnly: false };

  /**
   * Where a body with no elevation at any level stops descending, or null.
   *
   * Read off the registry row rather than off pyramid.json, so the stop is
   * there before the first tile fetch instead of appearing halfway down a
   * descent; `bake_pyramid.py --check` is what ties the manifest to these same
   * floor levels. One test says the whole thing because `readBodyRow` refuses a
   * row whose null height floor and `coverage.height` disagree.
   */
  function imageryFloor(id: string | null): ImageryFloor | null {
    const row = rowById(id);
    if (!row?.bake || row.bake.height_floor_level !== null) return null;
    // A cloud-top corridor body can carry a baked albedo role too (Jupiter's
    // Cassini map, for the orrery globe) without becoming an imagery-only
    // body: its floor is the corridor's own deck, read by `descentFloorM`.
    if (row.corridor !== null) return null;
    if (imageryFloorDebug.heightRoleFrom !== null && id === state.bodyId) return null;
    const level = row.bake.albedo_floor_level;
    if (level === null) return null;
    const radiusM = registryDatumM(row.id);
    return {
      level,
      gsdM: albedoGsd(level, radiusM),
      altitudeM: imageryFloorAltitudeM(level, radiusM),
    };
  }

  /**
   * The published shape model a body's height role IS, or null.
   *
   * Class (d) of 04-body-data-ladder.md: no raster DTM was ever published, so
   * the height pyramid is baked from the shape model itself and the drawn
   * surface is that model's own radii (`tools/dtm/shapemodel.py`). The answer
   * comes off the ROW, so the overlay says it at every band whether or not any
   * tile has streamed - the same reason the imagery floor and the corridor are
   * read off the row.
   */
  function shapeModelProduct(id: string | null): BakeGlobalProduct | null {
    const row = rowById(id);
    if (!row?.bake || row.bake.height_floor_level === null) return null;
    return row.bake.global.find(
      (g) => g.role === 'height'
        && (g.format === 'shape_grid' || g.format === 'shape_mesh'),
    ) ?? null;
  }

  /**
   * `descent-dojo --inject corridor-edge`: the corridor floor's own control.
   *
   * One scale height added to the LOWEST band's bottom edge, on the row, which
   * is the one number the whole class is built on - the camera's stop, the
   * band the rung titles print and the shell the view draws all follow it, so
   * a stop hardcoded to an altitude or to a body name cannot move with it.
   */
  const corridorDebug: { liftLowestBottom: boolean } = { liftLowestBottom: false };


  let corridorCache: { id: string; lifted: boolean; geo: CorridorGeometry } | null = null;
  /** `descent-dojo --manifest`'s stability bar: builds this cache missed. */
  let corridorBuilds = 0;

  /**
   * The cloud-top corridor a class (c) row declares, or null on every other.
   *
   * Derived once per body and held: `updateCorridor` reads it every frame and
   * the breadcrumb, the overlay and the camera floor all read the same object,
   * so one moved band edge moves the four together. The manifest panel does
   * NOT read a floor through this cache (see `descentFloorM`): it walks every
   * corridor body on a paint, and doing that here would evict whichever giant
   * is actually focused, so a repaint of the panel would cost the live view a
   * rebuild it never asked for.
   */
  function corridorFor(id: string | null): CorridorGeometry | null {
    const row = rowById(id);
    if (!row || row.corridor === null) return null;
    const lifted = corridorDebug.liftLowestBottom && id === state.bodyId;
    if (corridorCache && corridorCache.id === row.id && corridorCache.lifted === lifted) {
      return corridorCache.geo;
    }
    const geo = corridorGeometry(
      row.corridor, lifted ? row.corridor.scale_height_m : 0);
    corridorCache = { id: row.id, lifted, geo };
    corridorBuilds++;
    return geo;
  }

  /** The corridor band the camera is in, or null above the corridor's own top. */
  function corridorBandNow(): number | null {
    const geo = corridorFor(state.bodyId);
    return geo === null ? null : corridorBandIndexAt(geo, deps.focusRig.altitude());
  }

  /**
   * Where this body's own data stops the descent, metres, or null.
   *
   * The two classes with a floor above the ground answer here and nothing else
   * does: class (b) at its imagery floor, class (c) at the bottom edge of its
   * corridor. `camera.ts`'s `minAltitude` reads it before it reads
   * `availability.descend`, which is what lets a body with no surface be flown
   * below ORBIT without becoming a body with hexes.
   *
   * The manifest panel is the only caller and asks about every corridor body
   * in the registry, not the one in focus, so a corridor floor is built
   * straight off the row rather than through `corridorFor`'s single-entry
   * cache - going through it would evict the focused giant's own geometry on
   * every other giant's row.
   */
  function descentFloorM(id: string | null): number | null {
    const f = imageryFloor(id);
    // Smooth ground is still imagery-only - `imageryFloor` keeps saying so - but
    // the imagery no longer stops the eye.
    if (f !== null) return smoothGround(id) ? null : f.altitudeM;
    const row = rowById(id);
    if (!row || row.corridor === null) return null;
    return corridorGeometry(row.corridor, 0).floorAltitudeM;
  }

  /**
   * The deepest ladder rung a body's own floor still reaches.
   *
   * On the CAMERA's own radius (`datumRadiusM`), because that is the radius the
   * band edges it clamps against are derived from. Which is neither of the
   * registry's own numbers: a pyramid publishes the sphere its height product
   * is relief about (17.3 km inside Ceres's `ellipsoid.a_m`, 1.9 km inside
   * Phobos's), and a body with no pyramid falls back to `radius_m`, 1,581 km
   * under `a_m` on Jupiter, where it is the volumetric mean 1 bar radius.
   */
  const floorTier = (): number => {
    const f = descentFloorM(state.bodyId);
    if (f === null) return 6;
    return tierAt(f, deps.surfaceRadius.datumRadiusM(state.bodyId) ?? registryDatumM(state.bodyId));
  };

  let climateCache: { id: string; climate: PlotClimate; missing: readonly string[] } | null = null;
  /**
   * The focused body's season, solar day, solar constant and dust curve.
   *
   * Null only before the registry has landed or with nothing focused. A row
   * that HAS landed and is missing one of those fields reads through
   * `softClimate` instead of refusing: the Atlas opens every body, and a gap
   * costs that body its season rather than its globe. The warning is once per
   * body, off this cache, and `skyDebug.seasonMissing` carries the same
   * answer for an instrument. The PLOT path does not come through here - it
   * calls `plotClimate` and still throws, where a substituted season would be
   * a lie about the ground.
   */
  function bodyClimate(): PlotClimate | null {
    const id = state.bodyId;
    if (id === null) return null;
    if (climateCache?.id !== id) {
      const row = rowById(id);
      if (!row) return null;
      const soft = softClimate(row, rowById);
      climateCache = { id, climate: soft.climate, missing: soft.missing };
      if (soft.missing.length > 0) {
        console.warn(`body "${id}" has no ${soft.missing.join(', no ')}: `
          + 'the Atlas opens it with a mean-distance, zero-declination season '
          + '(public/art/atlas/bodies.json)');
      }
    }
    return climateCache.climate;
  }

  return {
    bodyMesh,
    rowById,
    requestSets,
    ladderBody,
    registryDatumM,
    reliefAboveDatumM,
    bodyQuads,
    quadFor,
    descendable,
    imageryFloorDebug,
    smoothGround,
    artificialDebug,
    imageryFloor,
    shapeModelProduct,
    corridorDebug,
    get corridorCache() { return corridorCache; },
    set corridorCache(v) { corridorCache = v; },
    get corridorBuilds() { return corridorBuilds; },
    corridorFor,
    corridorBandNow,
    descentFloorM,
    floorTier,
    bodyClimate,
  };
}
